'use client'

import { useEffect } from 'react'

/**
 * Chrome and Firefox treat a mouse-wheel event over a focused
 * `<input type="number">` as a spin: the value ticks by ±step. Scrolling
 * a long form to reach the Save button can silently change the focused
 * field — this is how a $245.67 rate became $245.66 in the payer panel.
 *
 * Implementation: a document-level focusin/focusout pair attaches a
 * NON-passive wheel listener directly to a number input only while it is
 * focused. preventDefault() deterministically cancels the spin (a passive
 * document listener can't), and the blur() hands scrolling back to the
 * page for subsequent ticks. Because the wheel listener exists only on
 * the focused input, page scroll performance everywhere else is
 * unaffected. Mounted once in the root layout; covers every current and
 * future number input without per-input handlers.
 */
export function NumberInputWheelGuard() {
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      ;(e.currentTarget as HTMLInputElement).blur()
    }
    const isNumberInput = (t: EventTarget | null): t is HTMLInputElement =>
      t instanceof HTMLInputElement && t.type === 'number'
    const onFocusIn = (e: FocusEvent) => {
      if (isNumberInput(e.target)) e.target.addEventListener('wheel', onWheel, { passive: false })
    }
    const onFocusOut = (e: FocusEvent) => {
      if (isNumberInput(e.target)) e.target.removeEventListener('wheel', onWheel)
    }
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])
  return null
}
